import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, PhoneCall, ArrowRight, LogOut, User, Shield, TrendingUp } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';
import { NotificationCenter } from './NotificationCenter';
import mainLogo from '../assets/images/home/mainLogo.svg';

let analyticsModulePromise: Promise<typeof import('../services/analyticsService')> | null = null;

function trackClick(
  elementId: string,
  label: string,
  path: string,
  userId?: string,
  metadata?: Record<string, any>
) {
  if (!import.meta.env.PROD) return;
  try {
    const p = (analyticsModulePromise ??= import('../services/analyticsService'));
    void p.then(({ analyticsService }) => analyticsService.trackClick(elementId, label, path, userId, metadata));
  } catch {
    // no-op
  }
}

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, userData, isAdmin, hasPermission, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    {
      name: 'Platform',
      path: '/platform',
      dropdown: [
        { name: 'VoIP Services', path: '/platform/voip-services' },
        { name: 'Cloud PBX Platform', path: '/platform/cloud-pbx' },
        { name: 'Voice Virtual AI Agents', path: '/platform/virtual-agents' },
        { name: 'Smart AI Routing & Billing', path: '/platform/ai-routing' },
        { name: 'Auto Dialer', path: '/platform/predictive-dialer' },
      ],
    },
    {
      name: 'Solutions',
      path: '/solutions',
      dropdown: [
        { name: 'By Industry', path: '/solutions', isHeader: true },
        { name: 'Contact Centers', path: '/solutions/industry/contact-centers' },
        { name: 'BPOs', path: '/solutions/industry/bpos' },
        { name: 'Financial Services', path: '/solutions/industry/finance' },
        { name: 'Healthcare', path: '/solutions/industry/healthcare' },
        { name: 'By Business Size', path: '/solutions', isHeader: true },
        { name: 'Small Business (SMB)', path: '/solutions/size/smb' },
        { name: 'Large Enterprise', path: '/solutions/size/enterprise' },
      ],
    },
    { name: 'Integrations', path: '/integrations' },
    { name: 'Pricing', path: '/pricing' },
    {
      name: 'Resources',
      path: '/resources',
      dropdown: [
        { name: 'Docs and FAQ', path: '/resources/guides' },
      ],
    },
    { name: 'Contact', path: '/contact' },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isDropdownItemActive = (path: string) => location.pathname === path;

  const isNavLinkActive = (link: any) => {
    if (link.dropdown) {
      // Parent should be active on its own route (/platform) and on nested routes (/platform/*)
      return isActivePath(link.path) || link.dropdown.some((d: any) => !d.isHeader && isActivePath(d.path));
    }
    return isActivePath(link.path);
  };

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isScrolled ? 'bg-white/80 backdrop-blur-md py-3' : 'py-5'
        )}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 z-50">
              <img src={mainLogo} alt="Voxonics" width={122} height={65} decoding="async" className="w-25" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  {link.dropdown ? (
                    <Link 
                      to={link.path}
                      onClick={() => trackClick(`nav_${link.name.toLowerCase()}`, link.name, location.pathname, user?.uid)}
                      className="flex items-center gap-1 cursor-pointer py-2 group/link"
                    >
                      <span
                        className={cn(
                          "text-sm font-medium transition-colors",
                          isNavLinkActive(link) ? "text-indigo-600" : "text-gray-600 group-hover/link:text-indigo-600"
                        )}
                      >
                        {link.name}
                      </span>
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 transition-transform group-hover:rotate-180",
                          isNavLinkActive(link) ? "text-indigo-600" : (isScrolled ? "text-gray-400" : "text-gray-400 group-hover/link:text-indigo-600")
                        )}
                      />
                    </Link>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => trackClick(`nav_${link.name.toLowerCase()}`, link.name, location.pathname, user?.uid)}
                      className={cn(
                        "text-sm font-medium transition-colors py-2 block",
                        isNavLinkActive(link) ? "text-indigo-600" : (isScrolled ? "text-gray-600 hover:text-indigo-600" : "text-gray-600 lg:hover:text-indigo-600")
                      )}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Dropdown */}
                  {link.dropdown && (
                    <div className="absolute top-full left-0 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 min-w-[240px] flex flex-col gap-1">
                        {link.dropdown.map((item: any) => (
                          item.isHeader ? (
                            <div key={item.name} className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 first:mt-0">
                              {item.name}
                            </div>
                          ) : (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => trackClick(`nav_dropdown_${item.name.toLowerCase().replace(/\s+/g, '_')}`, item.name, location.pathname, user?.uid)}
                              className={cn(
                                "px-4 py-2.5 text-sm rounded-lg transition-colors",
                                isDropdownItemActive(item.path)
                                  ? "bg-indigo-50 text-indigo-600"
                                  : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                              )}
                            >
                              {item.name}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              {user && <NotificationCenter />}
              {user && hasPermission('view_users') && (
                <div className="flex items-center gap-4">
                  <Link
                    to="/admin"
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors",
                      isScrolled ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-600 hover:text-indigo-600"
                    )}
                  >
                    <Shield className="w-4 h-4" />
                    {isAdmin ? 'Admin' : 'Dashboard'}
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin/analytics"
                      className={cn(
                        "flex items-center gap-1 text-sm font-medium transition-colors",
                        isScrolled ? "text-slate-600 hover:text-indigo-600" : "text-slate-600 hover:text-indigo-600"
                      )}
                    >
                      <TrendingUp className="w-4 h-4" />
                      Insights
                    </Link>
                  )}
                </div>
              )}
              {user ? (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || ''} width={32} height={32} decoding="async" className="w-8 h-8 rounded-full border border-gray-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                    <span className={cn("text-sm font-medium", isScrolled ? "text-gray-700" : "text-gray-700")}>
                      {userData?.firstName || user.displayName || user.email?.split('@')[0]}
                    </span>
                  </div>
                  <Link
                    to="/profile"
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors relative",
                      isScrolled ? "text-gray-600 hover:text-indigo-600" : "text-gray-600 hover:text-indigo-600"
                    )}
                  >
                    <User className="w-4 h-4" />
                    Profile
                    {/* trial indicator is handled elsewhere (no firebase trialStatus here) */}
                    {false && (
                      <span className="absolute -top-2 -right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className={cn(
                      "flex items-center gap-1 text-sm font-medium transition-colors",
                      isScrolled ? "text-gray-600 hover:text-red-600" : "text-gray-600 hover:text-red-600"
                    )}
                  >
                    <LogOut className="w-4 h-4" />
                    Log out
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/auth"
                    className={cn(
                      "text-sm font-medium transition-colors",
                      isScrolled ? "text-gray-600 hover:text-gray-900" : "text-gray-600 hover:text-indigo-600"
                    )}
                  >
                    Log in
                  </Link>
                  <Link
                    to="/auth?mode=signup"
                    onClick={() => trackClick('nav_free_trial', 'Free Trial', location.pathname, user?.uid)}
                    className="cta-target bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors shadow-sm shadow-indigo-200 flex items-center gap-2"
                  >
                    Free Trial
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden relative z-50 p-2 text-gray-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className={cn("w-6 h-6", !isScrolled && "text-gray-900 lg:text-white")} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60] lg:hidden"
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              id="mobile-menu"
              className="fixed inset-y-0 right-0 h-[100dvh] w-[85%] max-w-sm bg-white z-[70] lg:hidden shadow-2xl flex flex-col"
            >
              <div className="p-6 flex items-center justify-between border-b border-gray-100">
                <Link to="/" className="flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
                    <PhoneCall className="w-5 h-5" />
                  </div>
                  <span className="text-lg font-bold text-gray-900">Voxonics</span>
                </Link>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain py-6 px-6">
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link, idx) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + idx * 0.05 }}
                    >
                      {link.dropdown ? (
                        <div className="flex flex-col gap-1 mb-4">
                          <Link
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={cn(
                              "px-4 py-3 text-sm font-bold flex items-center justify-between transition-colors",
                              isNavLinkActive(link) ? "text-indigo-600" : "text-gray-900 hover:text-indigo-600"
                            )}
                          >
                            {link.name}
                          </Link>
                          <div className="flex flex-col pl-4 gap-1 border-l-2 border-indigo-50 ml-4">
                            {link.dropdown.map((item: any) => (
                              item.isHeader ? (
                                <div key={item.name} className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 first:mt-0">
                                  {item.name}
                                </div>
                              ) : (
                                <Link
                                  key={item.name}
                                  to={item.path}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={cn(
                                    "px-4 py-2.5 text-sm transition-colors",
                                    isDropdownItemActive(item.path) ? "text-indigo-600" : "text-gray-600 hover:text-indigo-600"
                                  )}
                                >
                                  {item.name}
                                </Link>
                              )
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={link.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={cn(
                            "block px-4 py-3 text-base font-medium rounded-xl transition-all mb-1",
                            isNavLinkActive(link)
                              ? "bg-indigo-50 text-indigo-600"
                              : "text-gray-900 hover:bg-indigo-50 hover:text-indigo-600"
                          )}
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </nav>
              </div>

              <div className="p-6 border-t border-gray-100 bg-gray-50/50">
                <div className="flex flex-col gap-3">
                  {user && hasPermission('view_users') && (
                    <div className="flex flex-col gap-2">
                      <Link
                        to="/admin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full py-4 text-center text-sm font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-2xl shadow-sm hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <Shield className="w-4 h-4" /> {isAdmin ? 'Admin Dashboard' : 'Management Dashboard'}
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin/analytics"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="w-full py-4 text-center text-sm font-bold text-slate-600 bg-slate-50 border border-slate-100 rounded-2xl shadow-sm hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
                        >
                          <TrendingUp className="w-4 h-4" /> Visitor Insights
                        </Link>
                      )}
                    </div>
                  )}
                  {user ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-2xl">
                        {user.photoURL ? (
                          <img src={user.photoURL} alt={user.displayName || ''} width={40} height={40} decoding="async" className="w-10 h-10 rounded-full border border-gray-100" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                            <User className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900">{userData?.displayName || user.displayName || user.email?.split('@')[0]}</span>
                          <span className="text-xs text-gray-500">{user.email}</span>
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full py-4 text-center text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-2xl shadow-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <User className="w-4 h-4" /> My Profile
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full py-4 text-center text-sm font-bold text-red-600 bg-white border border-red-100 rounded-2xl shadow-sm hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <LogOut className="w-4 h-4" /> Log out
                      </button>
                    </div>
                  ) : (
                    <>
                      <Link
                        to="/auth"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full py-4 text-center text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-2xl shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        Log in
                      </Link>
                      <Link
                        to="/auth?mode=signup"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="cta-target w-full py-4 text-center text-sm font-bold text-white bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                      >
                        Start Free Trial <ArrowRight className="w-4 h-4" />
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
